/**
 * Bonus pickups on the grid.
 * Ported from `Bonus` (`Bonus.h`) and `PlaceBonus` / `testCollisionMotoBonus`
 * (`Jeu.c:480` / `Jeu.c:503`).
 *
 * Three kinds (`EffetBonus.h`):
 *   - CLEAN — wipes every trail on pickup (instant).
 *   - BOOST — armed on pickup, then on the bonus key: +speed for a while.
 *   - JUMP  — armed on pickup, then on the bonus key: phase through trails briefly.
 */

import { bikeBox, type Bike } from "./bike";
import { boxOf, overlaps } from "./collision";
import type { GridBounds } from "./collision";
import type { Rng } from "./rng";
import { BonusKind } from "./types";

export interface Bonus {
  x: number;
  y: number;
  w: number;
  h: number;
  kind: Exclude<BonusKind, typeof BonusKind.None>;
}

/** Index of the first bike overlapping `bonus`, or -1. `testCollisionMotoBonus`. */
export function bikeTouchingBonus(bonus: Bonus, bikes: readonly Bike[]): number {
  const bBox = boxOf(bonus.x, bonus.y, bonus.w, bonus.h);
  for (let i = 0; i < bikes.length; i++) {
    if (overlaps(bBox, bikeBox(bikes[i]!))) return i;
  }
  return -1;
}

/**
 * Picks a free spot for a new bonus (not overlapping any bike) and a random kind.
 * Returns `null` if no clear spot was found in a few tries.
 */
export function placeBonus(
  grid: GridBounds,
  size: number,
  bikes: readonly Bike[],
  rng: Rng,
): Bonus | null {
  const kinds = [BonusKind.Clean, BonusKind.Boost, BonusKind.Jump] as const;
  for (let attempt = 0; attempt < 12; attempt++) {
    const x = grid.x + rng.int(grid.width - size);
    const y = grid.y + rng.int(grid.height - size);
    const candidate: Bonus = { x, y, w: size, h: size, kind: kinds[rng.int(3)]! };
    if (bikeTouchingBonus(candidate, bikes) === -1) return candidate;
  }
  return null;
}
